import { useEffect, useRef, useState } from 'react';
import {
  CHAVE_CACHE_SNAPSHOT,
  INTERVALO_ATUALIZACAO_MS,
  INTERVALO_RELOGIO_MS,
  TIMEOUT_FETCH_MS,
  URL_STATUS,
} from '@/constants/status.constants';
import { calcularIdadeSegundos, classificarFrescor } from '@/helpers/status.helpers';
import { validarStatusPublico } from '@/schemas/status.schema';
import type { EstadoStatus, StatusPublico } from '@/types/status.types';

/**
 * Carrega e mantém atualizado o snapshot público de status.
 *
 * O comportamento sob falha é a razão de ser deste hook, não um extra. Uma falha de
 * rede nunca esvazia a tela: o último snapshot bom permanece visível e a página passa
 * a se declarar desatualizada. Quem consulta status durante um incidente prefere um
 * dado de cinco minutos atrás, rotulado como tal, a uma tela em branco.
 */

function lerCache(): StatusPublico | null {
  try {
    const bruto = sessionStorage.getItem(CHAVE_CACHE_SNAPSHOT);

    if (!bruto) {
      return null;
    }

    const resultado = validarStatusPublico(JSON.parse(bruto));

    return resultado.ok ? resultado.dados : null;
  } catch {
    return null;
  }
}

function gravarCache(status: StatusPublico): void {
  try {
    sessionStorage.setItem(CHAVE_CACHE_SNAPSHOT, JSON.stringify(status));
  } catch {
    // sessionStorage indisponível (modo privado, cota cheia) não é motivo para falhar.
  }
}

async function buscarStatus(sinal: AbortSignal): Promise<StatusPublico> {
  // O parâmetro fura cache intermediário de CDN e proxy no ciclo de polling.
  const url = `${URL_STATUS}${URL_STATUS.includes('?') ? '&' : '?'}t=${Date.now()}`;

  const resposta = await fetch(url, {
    signal: sinal,
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });

  if (!resposta.ok) {
    throw new Error(`O serviço de status respondeu ${resposta.status}.`);
  }

  const resultado = validarStatusPublico(await resposta.json());

  if (!resultado.ok) {
    throw new Error(resultado.motivo);
  }

  return resultado.dados;
}

export function useStatus(): EstadoStatus {
  const [status, setStatus] = useState<StatusPublico | null>(lerCache);
  const [fase, setFase] = useState<EstadoStatus['fase']>(() =>
    lerCache() ? 'pronto' : 'carregando',
  );
  const [erro, setErro] = useState<string | null>(null);
  const [atualizando, setAtualizando] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());

  const requisicaoEmCurso = useRef<AbortController | null>(null);
  const temSnapshot = useRef(status !== null);
  // Ponte para o botão "tentar agora" disparar o mesmo ciclo do polling sem
  // recriar o intervalo e os listeners a cada clique.
  const dispararAtualizacao = useRef<() => void>(() => {});

  temSnapshot.current = status !== null;

  useEffect(() => {
    let ativo = true;

    async function atualizar() {
      requisicaoEmCurso.current?.abort();

      const controlador = new AbortController();
      requisicaoEmCurso.current = controlador;

      const expiracao = setTimeout(() => controlador.abort(), TIMEOUT_FETCH_MS);

      if (ativo) {
        setAtualizando(true);
      }

      try {
        const dados = await buscarStatus(controlador.signal);

        if (!ativo) {
          return;
        }

        gravarCache(dados);
        setStatus(dados);
        setErro(null);
        setFase('pronto');
        setAgora(Date.now());
      } catch (excecao) {
        if (!ativo || controlador.signal.reason === 'substituida') {
          return;
        }

        const motivo =
          excecao instanceof DOMException && excecao.name === 'AbortError'
            ? 'A consulta ao serviço de status demorou demais.'
            : excecao instanceof Error
              ? excecao.message
              : 'Não foi possível consultar o serviço de status.';

        setErro(motivo);

        // Só vira tela de erro se nunca houve snapshot. Havendo, ele continua no ar
        // e a página apenas se marca como desatualizada.
        setFase(temSnapshot.current ? 'pronto' : 'erro');
      } finally {
        clearTimeout(expiracao);

        if (ativo) {
          setAtualizando(false);
        }
      }
    }

    dispararAtualizacao.current = atualizar;

    atualizar();

    const cronometro = setInterval(atualizar, INTERVALO_ATUALIZACAO_MS);

    function aoVoltarAFoco() {
      if (document.visibilityState === 'visible') {
        atualizar();
      }
    }

    document.addEventListener('visibilitychange', aoVoltarAFoco);
    window.addEventListener('online', atualizar);

    return () => {
      ativo = false;
      clearInterval(cronometro);
      document.removeEventListener('visibilitychange', aoVoltarAFoco);
      window.removeEventListener('online', atualizar);
      dispararAtualizacao.current = () => {};
      requisicaoEmCurso.current?.abort('substituida');
    };
  }, []);

  // Relógio próprio: a idade do snapshot precisa envelhecer na tela mesmo quando
  // nenhuma atualização chega. É justamente quando nada chega que isso importa.
  useEffect(() => {
    const cronometro = setInterval(() => setAgora(Date.now()), INTERVALO_RELOGIO_MS);

    return () => clearInterval(cronometro);
  }, []);

  const idadeSegundos = calcularIdadeSegundos(status?.gerado_em, agora);

  return {
    fase,
    status,
    frescor: classificarFrescor(idadeSegundos),
    idadeSegundos,
    erro,
    atualizando,
    atualizarAgora: () => dispararAtualizacao.current(),
  };
}
